import register from 'preact-custom-element'
import { useState, useRef, useEffect, useMemo } from 'preact/hooks'
import Image from 'snippets/image/image'
import { newArrayReduce } from 'helpers/utils'
import useDetectBreakpoint from '@/components/hooks/useDetectBreakpoint'

function HomepageTabs() {
  const homeTabsConfig = window.homeTabsConfig
  const { isMobile } = useDetectBreakpoint()

  const [categoryOptions, setCategoryOptions] = useState([])

  useEffect(() => {
    const initialCategoryOptions = [...new Set(homeTabsConfig.map(data => data.category))].map(option => ({
      name: option
    }))
    setCategoryOptions(initialCategoryOptions)
  }, [])

  const newHomeTabsConfig = Object.keys(newArrayReduce(homeTabsConfig)).map(key => ({ [key]: newArrayReduce(homeTabsConfig)[key] }))

  const [activeTab, setActiveTab] = useState(0)
  const [imageHeight, setImageHeight] = useState(0)
  const refContent = useRef(null)

  const handleTabClick = (index) => {
    setActiveTab(index)
  }

  useEffect(() => {
    refContent.current.style.setProperty('--image-height', `${imageHeight}px`)
  }, [imageHeight])

  return (
    <div className="md:container">
      <div className="relative flex mb-4 text-center overflow-x-scroll scrollbar-hidden md:mb-12">
        {categoryOptions.map((tab, index) => (
          <button
            key={index}
            className={`relative flex-1 inline-block first:md-max:ml-4 last:md-max:mr-4 pb-[6px] text-base md:font-medium text-grey-700 before:absolute before:bottom-0 before:left-0 before:w-full before:h-[3px] before:bg-grey-100 before:content-[''] z-20 md-max:min-w-[152px] md:ml-0 md:px-4 md:pb-6 md:text-xl ${activeTab === index ? 'before:bg-primary' : ''}`}
            onClick={() => handleTabClick(index)}
          >
            {tab.name}
          </button>
        ))}
      </div>
      <div ref={refContent} className="relative">
        {newHomeTabsConfig.map((bannerData, index) => {
          const bannerItems = Object.values(bannerData)[0]
          return (
            <div className={`absolute md-max:container top-0 left-0 invisible w-full h-full opacity-0 z-20 transition-all duration-300 ease-linear ${activeTab === index ? 'relative !visible opacity-100' : ''}`}>
              {bannerItems.map((item, itemIndex) => (
                <BannerItems key={itemIndex} data={item} isMobile={isMobile} setImageHeight={setImageHeight} />
              ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function BannerItems({ data, isMobile, setImageHeight }) {
  const refImage = useRef(null)

  const imageUrl = (typeof data.image === 'string') ? data.image : data.image?.src

  const contentStyle = useMemo(() => {
    const paddingLeftOffset = isMobile ? Number(data.content_padding_left_mobile) : Number(data.content_padding_left)
    if (paddingLeftOffset < 0) {
      return { transform: `translateX(${paddingLeftOffset}px)` }
    }

    return { paddingLeft: `${paddingLeftOffset}px` }
  }, [data.content_padding_left, data.content_padding_left_mobile, isMobile])

  useEffect(() => {
    setImageHeight(refImage.current.clientHeight)
  }, [])

  return (
    <div className="flex flex-col items-center p-4 md:py-[60px] md:pr-[60px] md:pl-24 md:flex-row gap-6 xl:gap-[175px]" style={`background-color: ${data.bg_color};`}>
      <div className="flex flex-col w-full md:w-1/2 lg:max-w-[524px] 3xl:max-w-none" style={contentStyle}>
        <h3 className="h5 font-medium !text-grey-700 md:text-4xl md:tracking-normal">{data.title}</h3>
        <div
          className={`text-sm md:text-base mt-2 w-full md:mt-4 lg:max-w-[524px] wysiwyg--hometab-description ${data.cta_text && data.cta_url ? 'mb-6' : ''}`}
          dangerouslySetInnerHTML={{ __html: data.subtitle }}
        ></div>
        {data.cta_text && data.cta_url && (
          <a href={data.cta_url} className="relative w-fit inline-block text-base font-semibold text-primary capitalize transition-colors after:content-[''] after:w-full after:h-px after:absolute after:left-0 after:-bottom-0 after:bg-primary hover:text-primary-hover hover:after:bg-primary-hover" target={data.cta_new_tab ? '_blank' : '_self'}>
            <span>{data.cta_text}</span>
          </a>
        )}
      </div>
      {data.cta_url & data.image ? (
        <a href={data.cta_url} ref={refImage} className="block flex-1 w-full overflow-hidden z-10" target={data.cta_new_tab ? '_blank' : '_self'}>
          <Image
            sizes="(min-width: 1536px) 40vw, (min-width: 768px) 50vw, calc(100vw - 64px)"
            imageUrl={[imageUrl]}
            alt={data.title}
            className="aspect-[312/300] md:aspect-[489/496] relative"
            imageClass="fit object-cover transition-all duration-300 ease-in-out"
            isCover={false}
            isLazy={true}
          />
        </a>
      ) : (
        <div ref={refImage} className="block flex-1 w-full overflow-hidden z-10">
          <Image
            sizes="(min-width: 1536px) 40vw, (min-width: 768px) 50vw, calc(100vw - 64px)"
            imageUrl={[imageUrl]}
            alt={data.title}
            className="aspect-[312/300] md:aspect-[489/496] relative"
            imageClass="fit object-cover transition-all duration-300 ease-in-out"
            isCover={false}
            isLazy={true}
          />
        </div>
      )}
    </div>
  )
}

register(HomepageTabs, 'homepage-tabs')
